import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Hook to trap focus within a container element (e.g., modal, dialog)
 * Handles focus management for accessibility
 * @param {boolean} isActive - Whether the focus trap is active
 * @param {Object} options - Configuration options
 * @param {boolean} options.initialFocus - Whether to set initial focus (default: true)
 * @param {boolean} options.restoreFocus - Whether to restore focus on unmount (default: true)
 * @param {number} options.initialFocusDelay - Delay before setting initial focus in ms (default: 0)
 * @param {string} options.initialFocusSelector - CSS selector for initial focus target
 * @returns {Object} - Ref to attach to the container element
 */
const useFocusTrap = (isActive, options = {}) => {
  const {
    initialFocus = true,
    restoreFocus = true,
    initialFocusDelay = 0,
    initialFocusSelector = null
  } = options;

  const containerRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const hasRestoredRef = useRef(false);  // Prevent double restoration in React StrictMode
  const restoreFocusRef = useRef(restoreFocus);

  // Keep the latest restoreFocus value for cleanups triggered by mode switches.
  restoreFocusRef.current = restoreFocus;

  // Get all focusable elements within the container
  const getFocusableElements = () => {
    if (!containerRef.current) return [];
    
    const focusableSelectors = [
      'a[href]:not([disabled])',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(
      containerRef.current.querySelectorAll(focusableSelectors)
    ).filter(el => {
      // Filter out elements that are not visible or accessible
      const style = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        rect.width > 0 &&
        rect.height > 0 &&
        !el.hasAttribute('inert') &&
        !el.closest('[aria-hidden="true"]')
      );
    });
  };

  // Use useLayoutEffect for initial focus to prevent flicker
  useLayoutEffect(() => {
    if (!isActive || !containerRef.current) return;

    // Store the currently focused element before trap activates
    if (restoreFocus && !previousActiveElementRef.current) {
      previousActiveElementRef.current = document.activeElement;
      hasRestoredRef.current = false;  // Reset restoration flag
    }

    // Set initial focus
    if (initialFocus) {
      const setFocus = () => {
        // Try custom selector first
        if (initialFocusSelector) {
          const targetElement = containerRef.current.querySelector(initialFocusSelector);
          if (targetElement && !targetElement.disabled) {
            targetElement.focus({ preventScroll: true });
            return;
          }
        }
        
        // Fall back to the dialog container itself (WAI-ARIA compliant)
        // This prevents the jarring visual focus on the first interactive element
        if (!containerRef.current.hasAttribute('tabindex')) {
          containerRef.current.setAttribute('tabindex', '-1');
        }
        containerRef.current.focus({ preventScroll: true });
      };
      
      // Apply focus with optional delay (useful for animations)
      if (initialFocusDelay > 0) {
        const timeoutId = setTimeout(setFocus, initialFocusDelay);
        return () => clearTimeout(timeoutId);
      } else {
        setFocus();
      }
    }
  }, [isActive, initialFocus, restoreFocus, initialFocusDelay, initialFocusSelector]);

  // Use regular useEffect for event listeners and cleanup
  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    // Handle tab navigation
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) {
        e.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Tab backwards from first element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // Tab forward from last element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Add event listener
    const containerElement = containerRef.current;
    containerElement.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      if (containerElement) {
        containerElement.removeEventListener('keydown', handleKeyDown);
      }

      // Restore focus to previous element (with StrictMode protection)
      if (restoreFocusRef.current && previousActiveElementRef.current && !hasRestoredRef.current) {
        // Check if element still exists in DOM and is focusable
        if (document.body.contains(previousActiveElementRef.current)) {
          previousActiveElementRef.current.focus();
          hasRestoredRef.current = true;  // Prevent double restoration in StrictMode
        }
      }
    };
  }, [isActive, restoreFocus]);

  return containerRef;
};

export default useFocusTrap;
